import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, CheckCircle, XCircle, Send, X } from "lucide-react";
import { toast } from "sonner";

function money(v: string | number | null | undefined, currency = "EUR"): string {
  if (v == null) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("es-ES", { style: "currency", currency });
}

const ADJUSTMENT_STATUS: Record<string, string> = {
  draft: "Borrador",
  pending_approval: "Pendiente aprobación",
  approved: "Aprobado",
  rejected: "Rechazado",
  cancelled: "Cancelado",
};

const ADJUSTMENT_TYPES = [
  ["contract_extension", "Ampliación de contrato"],
  ["additional_work", "Trabajo adicional"],
  ["price_revision", "Revisión de precio"],
  ["contract_reduction", "Reducción de contrato"],
  ["other", "Otro"],
] as const;

interface AdjRow {
  id: number;
  title: string;
  direction: string;
  amount: string;
  status: string;
  effectiveDate: string | null;
  adjustmentType?: string;
  reason?: string;
  /** PR A: true when this adjustment is audit-only/superseded by a legacy base-price correction — excluded from every effective-economics sum below, but still shown here. */
  isAuditOnly?: boolean;
}

interface Props {
  contractId: number;
  canWriteEconomic: boolean;
  currency?: string;
}

export function ContractAdjustmentsSection({ contractId, canWriteEconomic, currency = "EUR" }: Props) {
  const utils = trpc.useUtils();
  const adjustments = trpc.customers.contracts.economyAdjustments.useQuery({ contractId }, { enabled: !!contractId });
  const createMut = trpc.customers.contracts.createAdjustment.useMutation();
  const updateMut = trpc.customers.contracts.updateAdjustment.useMutation();
  const submitMut = trpc.customers.contracts.submitAdjustment.useMutation();
  const approveMut = trpc.customers.contracts.approveAdjustment.useMutation();
  const rejectMut = trpc.customers.contracts.rejectAdjustment.useMutation();
  const cancelMut = trpc.customers.contracts.cancelAdjustment.useMutation();

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AdjRow | null>(null);
  const [rejectTarget, setRejectTarget] = useState<AdjRow | null>(null);
  const [title, setTitle] = useState("");
  const [reason, setReason] = useState("");
  const [adjType, setAdjType] = useState<"contract_extension" | "additional_work" | "price_revision" | "contract_reduction" | "other">("additional_work");
  const [direction, setDirection] = useState<"increase" | "decrease">("increase");
  const [amount, setAmount] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const rows = (adjustments.data ?? []) as AdjRow[];

  function invalidate() {
    utils.customers.contracts.economyAdjustments.invalidate({ contractId });
    utils.customers.contracts.economySummary.invalidate({ contractId });
  }

  function openCreate() {
    setTitle(""); setReason(""); setAdjType("additional_work"); setDirection("increase");
    setAmount(""); setEffectiveDate(""); setFormError(null);
    setCreateOpen(true);
  }

  function openEdit(a: AdjRow) {
    setTitle(a.title); setReason(a.reason ?? ""); setAmount(a.amount); setEffectiveDate(a.effectiveDate ?? "");
    setFormError(null);
    setEditTarget(a);
  }

  async function handleCreate() {
    if (!title.trim()) { setFormError("El título es obligatorio."); return; }
    if (!reason.trim()) { setFormError("La justificación es obligatoria."); return; }
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) { setFormError("Introduce un importe válido mayor que 0."); return; }
    setSaving(true); setFormError(null);
    try {
      await createMut.mutateAsync({
        contractId, title: title.trim(), reason: reason.trim(),
        adjustmentType: adjType, direction, amount: Number(amount).toFixed(2),
        effectiveDate: effectiveDate || null,
      });
      toast.success("Modificación creada");
      setCreateOpen(false);
      invalidate();
    } catch (e) {
      setFormError((e as { message?: string }).message ?? "Error al crear la modificación.");
    } finally { setSaving(false); }
  }

  async function handleUpdate() {
    if (!editTarget) return;
    setSaving(true); setFormError(null);
    try {
      await updateMut.mutateAsync({
        adjustmentId: editTarget.id, contractId,
        title: title.trim() || undefined,
        reason: reason.trim() || undefined,
        amount: amount ? Number(amount).toFixed(2) : undefined,
        effectiveDate: effectiveDate || null,
      });
      toast.success("Modificación actualizada");
      setEditTarget(null);
      invalidate();
    } catch (e) {
      setFormError((e as { message?: string }).message ?? "Error al actualizar.");
    } finally { setSaving(false); }
  }

  async function handleSubmit(a: AdjRow) {
    try {
      await submitMut.mutateAsync({ adjustmentId: a.id, contractId });
      toast.success("Modificación enviada para aprobación");
      invalidate();
    } catch (e) { toast.error((e as { message?: string }).message ?? "Error al enviar."); }
  }

  async function handleApprove(a: AdjRow) {
    try {
      await approveMut.mutateAsync({ adjustmentId: a.id, contractId });
      toast.success("Modificación aprobada");
      invalidate();
    } catch (e) { toast.error((e as { message?: string }).message ?? "Error al aprobar."); }
  }

  async function handleReject() {
    if (!rejectTarget) return;
    if (!rejectionReason.trim()) { toast.error("La razón de rechazo es obligatoria."); return; }
    try {
      await rejectMut.mutateAsync({ adjustmentId: rejectTarget.id, contractId, rejectionReason: rejectionReason.trim() });
      toast.success("Modificación rechazada");
      setRejectTarget(null);
      setRejectionReason("");
      invalidate();
    } catch (e) { toast.error((e as { message?: string }).message ?? "Error al rechazar."); }
  }

  async function handleCancel(a: AdjRow) {
    try {
      await cancelMut.mutateAsync({ adjustmentId: a.id, contractId });
      toast.success("Modificación cancelada");
      invalidate();
    } catch (e) { toast.error((e as { message?: string }).message ?? "Error al cancelar."); }
  }

  if (adjustments.isLoading) {
    return <div className="space-y-3"><Skeleton className="h-40 w-full" /></div>;
  }

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base">Modificaciones del contrato</CardTitle>
          {canWriteEconomic && (
            <Button size="sm" variant="outline" onClick={openCreate} className="min-h-[36px]">
              <Plus className="h-4 w-4 mr-1" />Nueva modificación
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No hay modificaciones para este contrato.</p>
          ) : (
            <div className="space-y-2">
              {rows.map((a) => (
                <div key={a.id} className="flex flex-wrap items-start justify-between gap-2 rounded-md border p-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{a.title}</div>
                    <div className="text-xs text-muted-foreground">{a.effectiveDate ?? "Sin fecha efectiva"}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <span className={`text-sm font-medium ${a.isAuditOnly ? "text-muted-foreground line-through" : a.direction === "increase" ? "text-green-600" : "text-red-600"}`}>
                      {a.direction === "increase" ? "+" : "−"}{money(a.amount, currency)}
                    </span>
                    <Badge variant={a.status === "approved" ? "default" : a.status === "rejected" ? "destructive" : "outline"}>
                      {ADJUSTMENT_STATUS[a.status] ?? "Estado registrado"}
                    </Badge>
                    {a.isAuditOnly && (
                      <Badge variant="outline" title="Superado por una corrección del importe base legacy; no suma en la economía vigente.">
                        Solo auditoría
                      </Badge>
                    )}
                    {canWriteEconomic && (
                      <div className="flex gap-1">
                        {a.status === "draft" && (
                          <>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(a)} title="Editar">
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleSubmit(a)} title="Enviar para aprobación">
                              <Send className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => handleCancel(a)} title="Cancelar">
                              <X className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                        {a.status === "pending_approval" && (
                          <>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-600" onClick={() => handleApprove(a)} title="Aprobar">
                              <CheckCircle className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={() => { setRejectTarget(a); setRejectionReason(""); }} title="Rechazar">
                              <XCircle className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nueva modificación</DialogTitle></DialogHeader>
          <AdjForm
            title={title} setTitle={setTitle}
            reason={reason} setReason={setReason}
            adjType={adjType} setAdjType={setAdjType}
            direction={direction} setDirection={setDirection}
            amount={amount} setAmount={setAmount}
            effectiveDate={effectiveDate} setEffectiveDate={setEffectiveDate}
            error={formError}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button disabled={saving} onClick={handleCreate}>{saving ? "Guardando..." : "Crear"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(v) => { if (!v) setEditTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Editar modificación</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Título</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={255} /></div>
            <div className="space-y-1.5"><Label>Justificación</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Importe</Label><Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Fecha efectiva</Label><Input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} /></div>
            {formError && <p className="text-sm text-destructive">{formError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Cancelar</Button>
            <Button disabled={saving} onClick={handleUpdate}>{saving ? "Guardando..." : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(v) => { if (!v) { setRejectTarget(null); setRejectionReason(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Rechazar modificación</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label>Razón del rechazo *</Label>
            <Textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} placeholder="Indica la razón del rechazo..." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleReject}>Rechazar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdjForm(props: {
  title: string; setTitle: (v: string) => void;
  reason: string; setReason: (v: string) => void;
  adjType: "contract_extension" | "additional_work" | "price_revision" | "contract_reduction" | "other";
  setAdjType: (v: "contract_extension" | "additional_work" | "price_revision" | "contract_reduction" | "other") => void;
  direction: "increase" | "decrease"; setDirection: (v: "increase" | "decrease") => void;
  amount: string; setAmount: (v: string) => void;
  effectiveDate: string; setEffectiveDate: (v: string) => void;
  error: string | null;
}) {
  return (
    <div className="space-y-3">
      <div className="space-y-1.5"><Label>Título *</Label><Input value={props.title} onChange={(e) => props.setTitle(e.target.value)} maxLength={255} /></div>
      <div className="space-y-1.5"><Label>Justificación *</Label><Textarea value={props.reason} onChange={(e) => props.setReason(e.target.value)} /></div>
      <div className="space-y-1.5">
        <Label>Tipo</Label>
        <Select value={props.adjType} onValueChange={(v) => props.setAdjType(v as typeof props.adjType)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {ADJUSTMENT_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label>Dirección</Label>
        <Select value={props.direction} onValueChange={(v) => props.setDirection(v as "increase" | "decrease")}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="increase">Incremento (+)</SelectItem>
            <SelectItem value="decrease">Reducción (−)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5"><Label>Importe *</Label><Input type="number" min="0" step="0.01" value={props.amount} onChange={(e) => props.setAmount(e.target.value)} /></div>
      <div className="space-y-1.5"><Label>Fecha efectiva</Label><Input type="date" value={props.effectiveDate} onChange={(e) => props.setEffectiveDate(e.target.value)} /></div>
      {props.error && <p className="text-sm text-destructive">{props.error}</p>}
    </div>
  );
}
